"use client";

import { toggleMatchReaction } from "@/actions/match";
import { toggleTournamentMessageReaction } from "@/actions/tournamentChat";
import { useAuth } from "@/contexts/AuthContext";
import { ReactionType } from "@/types";
import { Popover, PopoverContent, PopoverTrigger, Tooltip } from "@heroui/react";
import { Icon } from "@iconify/react";
import type { EmojiClickData, Theme } from "emoji-picker-react";
import { useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import dynamic from "next/dynamic";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const EmojiPicker = dynamic(() => import("emoji-picker-react"), {
  ssr: false,
  loading: () => (
    <div className="w-[300px] h-[400px] bg-[var(--bg-card)] rounded-lg" />
  ),
});

const QUICK_REACTIONS = ["👏", "😮", "💜", "💩"];

interface ReactionBarProps {
  matchId?: string;
  tournamentMessageId?: string;
  reactions?: Record<string, ReactionType>;
  compact?: boolean;
  /** Map of userId → display name for showing who reacted on hover */
  userNames?: Map<string, string>;
}

export function ReactionBar({
  matchId,
  tournamentMessageId,
  reactions,
  compact,
  userNames,
}: ReactionBarProps) {
  const { user } = useAuth();
  const t = useTranslations("reactions");
  const { resolvedTheme } = useTheme();
  const [showLoginTooltip, setShowLoginTooltip] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const isPendingRef = useRef(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const loginTooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const [localReactions, setLocalReactions] = useState<
    Record<string, ReactionType>
  >(reactions ?? {});

  useEffect(() => {
    setLocalReactions(reactions ?? {});
  }, [reactions]);

  useEffect(() => {
    return () => {
      if (loginTooltipTimerRef.current)
        clearTimeout(loginTooltipTimerRef.current);
    };
  }, []);

  const userReaction = user ? localReactions[user.id] : undefined;

  // Group reactions by emoji with counts, ordered by first appearance
  const groupedReactions = useMemo(() => {
    const groups: { emoji: string; count: number }[] = [];
    const seen = new Map<string, number>();

    for (const emoji of Object.values(localReactions)) {
      const idx = seen.get(emoji);
      if (idx !== undefined) {
        groups[idx].count++;
      } else {
        seen.set(emoji, groups.length);
        groups.push({ emoji, count: 1 });
      }
    }

    return groups;
  }, [localReactions]);

  const handleReaction = useCallback(
    async (emoji: string) => {
      if (!user || isPendingRef.current) return;

      const isRemoving = userReaction === emoji;
      const prev = { ...localReactions };
      const next = { ...localReactions };

      if (isRemoving) {
        delete next[user.id];
      } else {
        next[user.id] = emoji;
      }

      setLocalReactions(next);
      isPendingRef.current = true;
      setIsPending(true);

      const result = tournamentMessageId
        ? await toggleTournamentMessageReaction({
            messageId: tournamentMessageId,
            reaction: emoji,
          })
        : matchId
          ? await toggleMatchReaction({ matchId, reaction: emoji })
          : { success: false };

      if (!result.success) {
        setLocalReactions(prev);
      }

      isPendingRef.current = false;
      setIsPending(false);
    },
    [user, userReaction, localReactions, matchId, tournamentMessageId],
  );

  const handleAnonClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (loginTooltipTimerRef.current)
      clearTimeout(loginTooltipTimerRef.current);
    setShowLoginTooltip(true);
    loginTooltipTimerRef.current = setTimeout(
      () => setShowLoginTooltip(false),
      2000,
    );
  }, []);

  const handleEmojiSelect = useCallback(
    (emojiData: EmojiClickData) => {
      setPickerOpen(false);
      handleReaction(emojiData.emoji);
    },
    [handleReaction],
  );

  // Merge quick reactions with existing ones (show quick first, then any extras)
  // In compact mode, only show quick reactions that have counts to reduce clutter
  const allEmojis = useMemo(() => {
    const extras = groupedReactions.filter(
      (g) => !QUICK_REACTIONS.includes(g.emoji),
    );
    const quickEntries = QUICK_REACTIONS.map((emoji) => ({
      emoji,
      count: groupedReactions.find((g) => g.emoji === emoji)?.count || 0,
    }));
    return [
      ...(compact ? quickEntries.filter((e) => e.count > 0) : quickEntries),
      ...extras,
    ];
  }, [groupedReactions, compact]);

  // Get display names for users who reacted with a specific emoji
  const getNamesForEmoji = useCallback(
    (emoji: string): string[] | undefined => {
      if (!userNames) return undefined;
      const names: string[] = [];
      for (const [userId, reaction] of Object.entries(localReactions)) {
        if (reaction === emoji) {
          const name = userNames.get(userId);
          if (name) names.push(name);
        }
      }
      return names.length > 0 ? names : undefined;
    },
    [localReactions, userNames],
  );

  const btnSize = compact
    ? "min-h-[26px] min-w-[26px] px-1.5 text-xs"
    : "min-h-[36px] min-w-[36px] px-2 text-sm";

  const emojiSize = compact ? "text-sm" : "text-base";

  // In compact mode, hide the empty-state picker button by default and reveal
  // it when the surrounding message group is hovered/focused. This keeps the
  // chat calm while still making the reaction affordance discoverable.
  const hasAnyReactions = groupedReactions.length > 0;
  // On touch devices `group-hover` rarely fires, so the hide-until-hover
  // treatment would strand mobile users. The `hover-none` variant keeps the
  // button visible wherever hover isn't available.
  const pickerVisibility = compact && !hasAnyReactions
    ? "opacity-0 group-hover:opacity-60 group-focus-within:opacity-60 hover:!opacity-100 focus-visible:!opacity-100 [@media(hover:none)]:opacity-50"
    : "opacity-50 hover:opacity-80";

  const reactionButtons = (
    <div
      role="group"
      aria-label={t("label")}
      className={`flex items-center flex-wrap gap-1 ${compact ? "" : "mt-1 px-1"}`}
    >
      {allEmojis.map(({ emoji, count }) => {
        const isActive = userReaction === emoji;
        const hasCount = count > 0;
        const tooltipNames = hasCount ? getNamesForEmoji(emoji) : undefined;

        const btn = (
          <button
            key={emoji}
            type="button"
            disabled={isPending}
            onClick={(e) => {
              e.stopPropagation();
              if (!user) {
                handleAnonClick(e);
                return;
              }
              handleReaction(emoji);
            }}
            aria-label={`${emoji} ${hasCount ? count : ""}`}
            aria-pressed={isActive}
            className={`
              inline-flex items-center gap-0.5 rounded-full transition-all
              ${btnSize}
              ${
                isActive
                  ? "bg-primary/20 ring-1 ring-primary scale-105"
                  : hasCount
                    ? "bg-default-100 hover:bg-default-200"
                    : "opacity-50 hover:opacity-80 hover:bg-default-100"
              }
              cursor-pointer disabled:cursor-default
            `}
          >
            <span className={emojiSize}>{emoji}</span>
            {hasCount && (
              <span
                className={`font-medium ${isActive ? "text-primary" : "text-default-500"}`}
              >
                {count}
              </span>
            )}
          </button>
        );

        if (tooltipNames) {
          return (
            <Tooltip
              key={emoji}
              delay={400}
              content={
                <div className="max-h-[150px] overflow-y-auto py-1 px-0.5 custom-scrollbar">
                  {tooltipNames.map((name) => (
                    <div key={name} className="text-xs whitespace-nowrap">
                      {name}
                    </div>
                  ))}
                </div>
              }
            >
              {btn}
            </Tooltip>
          );
        }

        return btn;
      })}

      {/* Emoji picker trigger */}
      {user && (
        <Popover
          isOpen={pickerOpen}
          onOpenChange={setPickerOpen}
          placement="bottom"
        >
          <PopoverTrigger>
            <button
              type="button"
              disabled={isPending}
              onClick={(e) => e.stopPropagation()}
              aria-label={t("pickEmoji")}
              className={`
                inline-flex items-center justify-center rounded-full transition-opacity
                ${btnSize}
                ${pickerVisibility} hover:bg-default-100
                cursor-pointer disabled:cursor-default
              `}
            >
              <Icon
                icon="solar:smile-circle-linear"
                className={compact ? "text-sm" : "text-lg"}
              />
            </button>
          </PopoverTrigger>
          <PopoverContent className="p-0 bg-transparent shadow-none border-none">
            <div onClick={(e) => e.stopPropagation()}>
              <EmojiPicker
                onEmojiClick={handleEmojiSelect}
                theme={
                  (resolvedTheme === "light" ? "light" : "dark") as Theme
                }
                width={300}
                height={350}
                searchPlaceholder={t("searchEmoji")}
                lazyLoadEmojis
                categories={[
                  { category: "smileys_people" as never, name: "" },
                  { category: "animals_nature" as never, name: "" },
                  { category: "food_drink" as never, name: "" },
                  { category: "activities" as never, name: "" },
                  { category: "symbols" as never, name: "" },
                ]}
                previewConfig={{ showPreview: false }}
                skinTonesDisabled
              />
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );

  if (!user) {
    return (
      <Tooltip
        content={t("loginRequired")}
        isOpen={showLoginTooltip}
        size="sm"
        placement="bottom"
      >
        {reactionButtons}
      </Tooltip>
    );
  }

  return reactionButtons;
}
